import { useState, type FormEvent } from 'react'
import { api } from '@/api/client'
import { useFetch, useMutate } from '@/hooks/useApi'
import { useConfigQuery } from '@/context/ClubContext'
import {
  AsyncState,
  Button,
  ConfirmDialog,
  Field,
  Icon,
  IconButton,
  Input,
  Modal,
  PageHead,
  Select
} from '@/components/ui'
import { ClubGate } from '@/components/ClubGate'
import {
  RELAY_LETTERS_KEY,
  parseRelayLetters,
  type GameType,
  type SettingRow,
  type Table,
  type Tariff
} from '@/lib/config'

import { FloorPlanEditorTab } from '@/components/FloorMapEditor'

type Tab = 'tables' | 'gameTypes' | 'floorMap'

export function Tables(): React.JSX.Element {
  const [tab, setTab] = useState<Tab>('tables')
  return (
    <>
      <PageHead
        eyebrow="Конфигурация"
        title="Столы"
        lead="Игровые столы клуба: имя, контакт реле PowerAnt, позиция в зале, тариф по умолчанию и тип игры. Типы игр (русский бильярд, пул, снукер, теннис…) заводятся во вкладке «Типы игр»."
      />
      <ClubGate>
        <div className="toolbar">
          <div className="tabs">
            <button
              className={`tab${tab === 'tables' ? ' is-active' : ''}`}
              onClick={() => setTab('tables')}
            >
              Столы
            </button>
            <button
              className={`tab${tab === 'gameTypes' ? ' is-active' : ''}`}
              onClick={() => setTab('gameTypes')}
            >
              Типы игр
            </button>
            <button
              className={`tab${tab === 'floorMap' ? ' is-active' : ''}`}
              onClick={() => setTab('floorMap')}
            >
              План зала
            </button>
          </div>
        </div>
        {tab === 'tables' && <TablesTable />}
        {tab === 'gameTypes' && <GameTypesTable />}
        {tab === 'floorMap' && <FloorPlanEditorTab />}
      </ClubGate>
    </>
  )
}

function TablesTable(): React.JSX.Element {
  const query = useConfigQuery()
  const list = useFetch<Table[]>('/v1/tables', query, [query?.clubId])
  const tariffs = useFetch<Tariff[]>('/v1/tariffs', query, [query?.clubId])
  const gameTypes = useFetch<GameType[]>('/v1/game-types', query, [query?.clubId])
  const settings = useFetch<SettingRow[]>('/v1/settings', query, [query?.clubId])
  const [editing, setEditing] = useState<Table | 'new' | null>(null)
  const [deleting, setDeleting] = useState<Table | null>(null)
  const del = useMutate()

  const rows = list.data ?? []
  const tariffList = tariffs.data ?? []
  const gameTypeList = gameTypes.data ?? []
  const relayLetters = parseRelayLetters(
    settings.data?.find((s) => s.key === RELAY_LETTERS_KEY)?.value
  )
  const tariffName = (id?: string | null): string =>
    tariffList.find((t) => t.id === id)?.name ?? '—'
  const kindName = (code?: string | null): string =>
    code ? (gameTypeList.find((g) => g.code === code)?.name ?? code) : '—'

  const onDeleteConfirm = async (): Promise<void> => {
    if (!deleting) return
    const ok = await del.mutate(() => api.del(`/v1/tables/${deleting.id}`, { query }))
    if (ok) {
      setDeleting(null)
      list.reload()
    }
  }

  return (
    <>
      <div className="toolbar">
        <div className="toolbar__left">
          <span className="muted">{rows.length} стол(ов)</span>
        </div>
        <div className="toolbar__right">
          <Button variant="primary" size="sm" onClick={() => setEditing('new')}>
            {Icon.plus}
            Новый стол
          </Button>
        </div>
      </div>

      <AsyncState
        query={list}
        isEmpty={rows.length === 0}
        empty="Столы ещё не заданы. Добавьте первый."
      />

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Название</th>
                <th className="num">Реле</th>
                <th>Тип игры</th>
                <th>Онлайн-бронь</th>
                <th className="num">Позиция</th>
                <th>Тариф по умолчанию</th>
                <th style={{ width: 96 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id}>
                  <td>{t.name}</td>
                  <td className="num">{t.relay}</td>
                  <td>{kindName(t.kind)}</td>
                  <td>{t.onlineBookable === false ? '—' : '✓'}</td>
                  <td className="num">{t.position ?? '—'}</td>
                  <td>{tariffName(t.tariffId)}</td>
                  <td>
                    <div className="row-actions">
                      <IconButton title="Изменить" onClick={() => setEditing(t)}>
                        {Icon.edit}
                      </IconButton>
                      <IconButton danger title="Удалить" onClick={() => setDeleting(t)}>
                        {Icon.trash}
                      </IconButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <TableForm
          table={editing === 'new' ? null : editing}
          tables={rows}
          tariffs={tariffList}
          gameTypes={gameTypeList}
          relayLetters={relayLetters}
          query={query}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            list.reload()
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Удалить стол"
          message={`Удалить стол «${deleting.name}»? Действие нельзя отменить.`}
          busy={del.saving}
          error={del.error}
          onConfirm={() => void onDeleteConfirm()}
          onCancel={() => setDeleting(null)}
        />
      )}
    </>
  )
}

function TableForm({
  table,
  tables,
  tariffs,
  gameTypes,
  relayLetters,
  query,
  onClose,
  onSaved
}: {
  table: Table | null
  tables: Table[]
  tariffs: Tariff[]
  gameTypes: GameType[]
  relayLetters: string[]
  query: ReturnType<typeof useConfigQuery>
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const isEdit = table !== null
  const [name, setName] = useState(table?.name ?? '')
  const [relay, setRelay] = useState(table ? String(table.relay).toUpperCase() : '')
  const [position, setPosition] = useState(table?.position != null ? String(table.position) : '')
  const [kind, setKind] = useState<string>(table?.kind ?? '')
  // По умолчанию стол доступен онлайн (undefined трактуем как true — совпадает с DEFAULT колонки).
  const [onlineBookable, setOnlineBookable] = useState<boolean>(table?.onlineBookable !== false)
  const [tariffId, setTariffId] = useState(table?.tariffId ?? '')
  const [allowed, setAllowed] = useState<string[]>(
    table?.tariffIds ?? (table?.tariffId ? [table.tariffId] : [])
  )
  const [validation, setValidation] = useState<string | null>(null)
  const save = useMutate()

  const toggleAllowed = (id: string): void =>
    setAllowed((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]))

  // Какой стол уже занимает данное реле (кроме редактируемого) — чтобы не назначить дважды.
  const relayOwner = (letter: string): Table | undefined =>
    tables.find((t) => t.id !== table?.id && String(t.relay).toUpperCase() === letter)

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return setValidation('Укажите название стола.')
    const relayLetter = relay.trim().toUpperCase()
    if (!relayLetters.includes(relayLetter))
      return setValidation(
        'Выберите контакт реле из списка (настраивается в «Настройки» → «Реле PowerAnt»).'
      )
    const posNum = position.trim() === '' ? null : Number(position)
    if (posNum !== null && (!Number.isInteger(posNum) || posNum < 0))
      return setValidation('Позиция должна быть неотрицательным целым числом.')
    setValidation(null)

    const body = {
      name: trimmed,
      relay: relayLetter,
      position: posNum,
      tariffId: tariffId || null,
      kind: kind || null,
      onlineBookable
    }

    const ok = await save.mutate(async () => {
      const saved = isEdit
        ? await api.patch<Table>(`/v1/tables/${table.id}`, body, { query })
        : await api.post<Table>('/v1/tables', body, { query })
      // Назначаем доступные тарифы отдельным запросом (PUT .../tariffs).
      const id = saved?.id ?? table?.id
      if (id) await api.put(`/v1/tables/${id}/tariffs`, { tariffIds: allowed }, { query })
    })
    if (ok) onSaved()
  }

  return (
    <Modal
      title={isEdit ? 'Изменить стол' : 'Новый стол'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button" disabled={save.saving}>
            Отмена
          </Button>
          <Button variant="primary" busy={save.saving} type="submit" form="table-form">
            Сохранить
          </Button>
        </>
      }
    >
      <form id="table-form" onSubmit={(e) => void onSubmit(e)}>
        <div className="form-grid">
          <Field label="Название" htmlFor="tb-name" full>
            <Input
              id="tb-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="напр. Стол 1"
              autoFocus
            />
          </Field>
          <Field label="Реле" htmlFor="tb-relay">
            <Select id="tb-relay" value={relay} onChange={(e) => setRelay(e.target.value)}>
              <option value="">— выберите реле —</option>
              {relayLetters.map((r) => {
                const owner = relayOwner(r)
                return (
                  <option key={r} value={r} disabled={!!owner}>
                    {owner ? `${r} — занято: ${owner.name}` : `${r} — свободно`}
                  </option>
                )
              })}
            </Select>
            <span className="field__hint">
              Контакты реле подключены к столам произвольно — список показывает, какие буквы уже
              заняты. Чтобы определить контакт стола, включите его свет в терминале (страница
              «Столы») и посмотрите, какой стол загорелся.
            </span>
          </Field>
          <Field label="Позиция" htmlFor="tb-pos">
            <Input
              id="tb-pos"
              type="number"
              min={0}
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              placeholder="порядок в зале"
            />
          </Field>
          <Field label="Тип игры" htmlFor="tb-kind" full>
            <Select id="tb-kind" value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="">— универсальный —</option>
              {gameTypes.map((g) => (
                <option key={g.code} value={g.code}>
                  {g.name}
                </option>
              ))}
            </Select>
            {gameTypes.length === 0 && (
              <span className="field__hint">
                Типы игр ещё не заданы — добавьте их во вкладке «Типы игр».
              </span>
            )}
          </Field>
          <div className="field field--full">
            <label style={{ display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={onlineBookable}
                onChange={(e) => setOnlineBookable(e.target.checked)}
              />
              <span className="field__label" style={{ margin: 0 }}>
                Доступен для онлайн-брони
              </span>
            </label>
            <span className="field__hint">
              Если выключено — стол не показывается на публичном сайте и в Telegram Mini App (но его
              тип игры онлайн скрывается, только если это последний бронируемый стол типа).
            </span>
          </div>
          <Field label="Тариф по умолчанию" htmlFor="tb-tariff" full>
            <Select id="tb-tariff" value={tariffId} onChange={(e) => setTariffId(e.target.value)}>
              <option value="">— не выбран —</option>
              {tariffs.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Доступные тарифы" full>
            {tariffs.length === 0 ? (
              <span className="field__hint">Сначала создайте тарифы.</span>
            ) : (
              <div className="days">
                {tariffs.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className={`day-chip${allowed.includes(t.id) ? ' is-on' : ''}`}
                    aria-pressed={allowed.includes(t.id)}
                    onClick={() => toggleAllowed(t.id)}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            )}
          </Field>
        </div>

        {validation && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {validation}
          </div>
        )}
        {save.error && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {save.error}
          </div>
        )}
      </form>
    </Modal>
  )
}

// ═══════════════════════════════ Типы игр ═══════════════════════════════

function GameTypesTable(): React.JSX.Element {
  const query = useConfigQuery()
  const list = useFetch<GameType[]>('/v1/game-types', query, [query?.clubId])
  const [editing, setEditing] = useState<GameType | 'new' | null>(null)
  const [deleting, setDeleting] = useState<GameType | null>(null)
  const del = useMutate()

  const rows = [...(list.data ?? [])].sort(
    (a, b) => (a.position ?? 0) - (b.position ?? 0) || a.name.localeCompare(b.name)
  )

  const onDeleteConfirm = async (): Promise<void> => {
    if (!deleting) return
    const ok = await del.mutate(() => api.del(`/v1/game-types/${deleting.id}`, { query }))
    if (ok) {
      setDeleting(null)
      list.reload()
    }
  }

  return (
    <>
      <div className="toolbar">
        <div className="toolbar__left">
          <span className="muted">{rows.length} тип(ов) игр</span>
        </div>
        <div className="toolbar__right">
          <Button variant="primary" size="sm" onClick={() => setEditing('new')}>
            {Icon.plus}
            Новый тип игры
          </Button>
        </div>
      </div>

      <AsyncState
        query={list}
        isEmpty={rows.length === 0}
        empty="Типы игр ещё не заданы. Добавьте первый (напр. «Русский бильярд»)."
      />

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Название</th>
                <th className="num">Позиция</th>
                <th style={{ width: 96 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((g) => (
                <tr key={g.id}>
                  <td>
                    {g.color && <span className="swatch" style={{ background: g.color }} />}
                    {g.name}
                  </td>
                  <td className="num">{g.position ?? '—'}</td>
                  <td>
                    <div className="row-actions">
                      <IconButton title="Изменить" onClick={() => setEditing(g)}>
                        {Icon.edit}
                      </IconButton>
                      <IconButton danger title="Удалить" onClick={() => setDeleting(g)}>
                        {Icon.trash}
                      </IconButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <GameTypeForm
          gameType={editing === 'new' ? null : editing}
          query={query}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            list.reload()
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Удалить тип игры"
          message={`Удалить тип игры «${deleting.name}»? Столы с этим типом станут универсальными.`}
          busy={del.saving}
          error={del.error}
          onConfirm={() => void onDeleteConfirm()}
          onCancel={() => setDeleting(null)}
        />
      )}
    </>
  )
}

function GameTypeForm({
  gameType,
  query,
  onClose,
  onSaved
}: {
  gameType: GameType | null
  query: ReturnType<typeof useConfigQuery>
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const isEdit = gameType !== null
  const [name, setName] = useState(gameType?.name ?? '')
  const [color, setColor] = useState(gameType?.color ?? '#c9a84c')
  const [position, setPosition] = useState(
    gameType?.position != null ? String(gameType.position) : ''
  )
  const [validation, setValidation] = useState<string | null>(null)
  const save = useMutate()

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return setValidation('Укажите название типа игры.')
    const posNum = position.trim() === '' ? null : Number(position)
    if (posNum !== null && (!Number.isInteger(posNum) || posNum < 0))
      return setValidation('Позиция должна быть неотрицательным целым числом.')
    setValidation(null)

    const body = { name: trimmed, color: color || null, position: posNum }
    const ok = await save.mutate(() =>
      isEdit
        ? api.patch(`/v1/game-types/${gameType.id}`, body, { query })
        : api.post('/v1/game-types', body, { query })
    )
    if (ok) onSaved()
  }

  return (
    <Modal
      title={isEdit ? 'Изменить тип игры' : 'Новый тип игры'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button" disabled={save.saving}>
            Отмена
          </Button>
          <Button variant="primary" busy={save.saving} type="submit" form="game-type-form">
            Сохранить
          </Button>
        </>
      }
    >
      <form id="game-type-form" onSubmit={(e) => void onSubmit(e)}>
        <div className="form-grid">
          <Field label="Название" htmlFor="gt-name" full>
            <Input
              id="gt-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="напр. Снукер"
              autoFocus
            />
          </Field>
          <Field label="Позиция" htmlFor="gt-pos">
            <Input
              id="gt-pos"
              type="number"
              min={0}
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              placeholder="порядок"
            />
          </Field>
          <Field label="Цвет" htmlFor="gt-color">
            <Input
              id="gt-color"
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
            />
          </Field>
        </div>

        {validation && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {validation}
          </div>
        )}
        {save.error && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {save.error}
          </div>
        )}
      </form>
    </Modal>
  )
}
